import { memo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link } from 'react-router';
import type { ClaimTableRow, LinkedDocument } from '@claim-os/shared';
import { StatusBadge, formatDateMMDDYYYY } from '@claim-os/shared';

interface SuccessPageProps {
  /** The created encounter's rows, display-ready, exactly as the claims list shows them. */
  claimRows: ClaimTableRow[];
  /** Documents returned from `POST /api/v1/encounters`. By the time this page
   *  renders, every file is committed (024 FR-003 all-or-nothing). */
  documentRows: LinkedDocument[];
  patientFirstName: string;
  patientLastName: string;
  /** Encounter date of service as ISO `YYYY-MM-DD`; may be empty when the
   *  practice configures the field as optional (feature 043). */
  dateOfService: string;
  /**
   * Reset the form and dismiss the success view so the user can submit another claim.
   * Used because we're already on /submit — a router Link to /submit would no-op.
   */
  onSubmitAnotherClaim: () => void;
}

/** Divider + padding chrome shared by every body cell in the claims table. */
const bodyCellSx = {
  borderBottom: '1px solid',
  borderColor: 'rowDivider',
  py: 2,
} as const;

/** Format bytes as a short human-readable string ("12 KB", "1 MB"). */
function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(0))} ${sizes[i]}`;
}

function SuccessPageImpl({
  claimRows,
  documentRows,
  patientFirstName,
  patientLastName,
  dateOfService,
  onSubmitAnotherClaim,
}: SuccessPageProps) {
  const patientName = `${patientFirstName} ${patientLastName}`.trim();
  // Encounter-level, so the same value repeats on every row.
  const dosLabel = formatDateMMDDYYYY(dateOfService);

  const rowCount = claimRows.length;
  const countLine =
    rowCount === 1
      ? '1 claim was created from your submission.'
      : `${rowCount} claims were created from your submission.`;

  const showDocSection = documentRows.length > 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Success!
      </Typography>

      <Paper sx={{ p: 4 }}>
        <Stack alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'colors.successHighlight',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'text.primary' }} />
          </Box>
          <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'center' }}>
            Patient Encounter Submitted Successfully
          </Typography>
          <Stack spacing={0.5} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Your patient encounter was successfully submitted. Thank you!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {countLine}
            </Typography>
          </Stack>
        </Stack>

        <TableContainer
          sx={{
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                {(['CLAIM #', 'PATIENT', 'PROVIDER', 'DOS', 'CLAIM TYPE', 'CREATED', 'STATUS'] as const).map((label) => (
                  <TableCell
                    key={label}
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: 12,
                      letterSpacing: 0.5,
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1.5,
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& tr:last-of-type td': { borderBottom: 0 } }}>
              {/* Provider rows share a number and carry no id; the table is static. */}
              {claimRows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ ...bodyCellSx, color: 'text.primary', whiteSpace: 'nowrap' }}>
                    {row.number ?? '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      color: 'text.primary',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                    }}
                  >
                    {patientName}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: 'text.primary' }}>
                    {row.providerName ?? '—'}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: 'text.primary', whiteSpace: 'nowrap' }}>
                    {dosLabel}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: 'text.primary', whiteSpace: 'nowrap' }}>
                    {row.claimType}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: 'text.primary' }}>
                    {formatDateMMDDYYYY(row.createdAt)}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <StatusBadge status={row.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {showDocSection && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Uploaded Documents
            </Typography>
            <Stack spacing={1}>
              {documentRows.map((doc) => (
                <Box
                  key={doc.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    minHeight: 52,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {doc.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {formatFileSize(doc.size)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          What would you like to do next?
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Button variant="outlined" component={Link} to="/claims" sx={{ height: 36 }}>
            Go to Submitted Claims
          </Button>
          <Button variant="contained" color="primary" onClick={onSubmitAnotherClaim} sx={{ height: 36 }}>
            Submit Another Claim
          </Button>
        </Stack>

        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{
            textAlign: 'left',
            bgcolor: 'colors.successHighlight',
            color: 'text.primary',
            '& .MuiAlert-icon': { color: 'text.primary' },
          }}
        >
          <Typography variant="body2">
            <strong>Go to Submitted Claims</strong>: Review claim statuses on submitted claims and
            upload additional documents.
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
}

const SuccessPage = memo(SuccessPageImpl);
export default SuccessPage;
